//api.ts
import { isTokenExpired, createTokenExpiredError } from '../utils/tokenValidation';
import { showTokenExpiredNotification } from '../utils/notificationService';
import { dedupeGetRequest } from '../utils/requestDedupe';

export const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] || 
  (import.meta.env.DEV ? '/api' : 'https://apijiggle.safebima.com/api');

export const buildApiUrl = (endpoint: string): string =>
  `${API_BASE_URL}/${endpoint.replace(/^\/+/, '')}`;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export async function apiFetch<T>(
  endpoint: string,
  method: HttpMethod = 'GET',
  body?: FormData | URLSearchParams | Record<string, unknown>,
  options?: { useCredentials?: boolean },
): Promise<T> {
  const token = localStorage.getItem('jiggle_token');
  const headers = new Headers();

  // Add CORS headers
  headers.append('Accept', 'application/json');
  headers.append('Content-Type', 'application/json');

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  // Determine if we should use credentials based on endpoint and options
  const shouldUseCredentials = options?.useCredentials !== false && 
    !endpoint.includes('login') && // Don't use credentials for login
    !endpoint.includes('register'); // Don't use credentials for register

  const requestOptions: RequestInit = { 
    method, 
    headers,
    mode: 'cors',
    cache: 'no-cache',
  };

  // Only add credentials if we should use them
  if (shouldUseCredentials) {
    requestOptions.credentials = 'include';
  }

  if (body instanceof FormData) {
    // For FormData, don't set Content-Type - let browser set it with boundary
    headers.delete('Content-Type');
    requestOptions.body = body;
  } else if (body instanceof URLSearchParams) {
    headers.set('Content-Type', 'application/x-www-form-urlencoded');
    requestOptions.body = body;
  } else if (body) {
    requestOptions.body = JSON.stringify(body);
  }

  const url = buildApiUrl(endpoint);

  const runFetch = async (): Promise<T> => {
    const response = await fetch(url, requestOptions);

    let responseData: unknown;

    try {
      responseData = await response.json();
    } catch {
      responseData = { message: 'Invalid JSON response from server' };
    }

    if (!response.ok) {
      const errorMessage =
        (typeof responseData === 'object' &&
          responseData !== null &&
          'message' in responseData &&
          typeof (responseData as { message: unknown }).message === 'string'
          ? (responseData as { message: string }).message
          : `API error: ${response.status} - ${response.statusText}`);

      if (responseData && typeof responseData === 'object' && 'status' in responseData) {
        const apiResponse = responseData as { status: 'success' | 'error'; message: string };
        if (isTokenExpired(apiResponse)) {
          const tokenError = createTokenExpiredError(errorMessage);
          showTokenExpiredNotification(errorMessage);
          setTimeout(() => {
            localStorage.removeItem('jiggle_token');
            localStorage.removeItem('jiggle_user');
            window.location.href = '/login';
          }, 2000);
          throw tokenError;
        }
      }

      throw new Error(errorMessage);
    }

    return responseData as T;
  };

  try {
    if (method === 'GET') {
      return await dedupeGetRequest(url, runFetch);
    }
    return await runFetch();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Network error or server is unreachable.');
    }
    throw error;
  }
}
